// @flow
import React from 'react'
import * as ids from '../constants/ids'
import * as routes from '../constants/routes'
import * as actionTypes from '../constants/action_types'

export type ContextBarItemType = {
    items: Array<{
        badge?: {
            content: string,
            color?: string,
        },
        collaborationEnabled?: boolean,
        content: any,
        icon: { default: string },
        id: string,
        isActive: Function | boolean,
        isEnabled?: boolean,
        name: string,
        link: string,
        sessions?: [],
    }>,
    // For now I'm going to store the current token locally, but I suspect there is a way to get it from the session or URL
    token: any,
}

function isActive(location: { path: string }, item: { link: string, active: boolean }, exactMatch: boolean = false) {
    if (item.link) {
        return exactMatch
            ? location.path === item.link
            : location.path.startsWith(item.link)
    }
    return item.active
}

function sessionsExist(sessions) {
    return sessions && sessions.length > 0
}

function isHosting(sessions, token) {
    return sessionsExist(sessions) && sessions[0].token === token
}

/**
 * Truncate session name to fit in the badge
 * @param sessions
 */
function getSessionName(sessions, notruncate) {
    let name = sessions.length ? sessions[0].name : ''
    if (name.length > 3 && !notruncate) {
        name = `${name.substr(0, 3)}..`
    }
    return name
}

function getBadgeContent(sessions, isListening, token, notruncate) {
    // Check if this session is being shared
    let ret = ''
    if (isHosting(sessions, token)) {
        // ret = 'fa fa-bullhorn'
        ret = getSessionName(sessions, notruncate)
    } else if (sessionsExist(sessions) && isListening) {
        // ret = 'fa fa-headphones'
        ret = getSessionName(sessions, notruncate)
    } else if (sessionsExist(sessions)) {
        ret = 'fa fa-bell'
    }
    return ret
}

function getBadgeColor(sessions, isListening, token) {
    let ret = 'primary'
    if (isHosting(sessions, token)) {
    } else if (sessionsExist(sessions) && isListening) {
        ret = 'secondary'
    } else if (sessionsExist(sessions)) {
    }
    return ret
}

const initialState = {
    items: [{
        icon: { default: 'fa fa-home' },
        id: ids.DOCUMENTATION_LINK,
        isActive: (location: { path: string }) => location.path === routes.HOME
            || location.path.startsWith(routes.INSTRUCTIONS),
        name: 'Documentation',
        link: routes.HOME,
    }, {
        icon: { default: 'fa fa-map' },
        id: ids.SELECT_FRAMEWORK,
        isActive,
        name: 'Select Theme',
        link: routes.SELECT_FRAMEWORK,
    }, {
        icon: { default: 'fa fa-bookmark' },
        id: ids.SELECT_BOOKMARK,
        isActive,
        name: 'Bookmarks',
        link: routes.SELECT_BOOKMARK,
    }, {
        icon: { default: 'fa fa-map-marker' },
        id: ids.ANALYZE_FRAMEWORK,
        isActive,
        isEnabled: false,
        name: 'Analyze Data',
        link: routes.ANALYZE_FRAMEWORK,
    }, {
        icon: { default: 'fa fa-info-circle' },
        id: ids.FRAMEWORK_INFO,
        isActive,
        isEnabled: false,
        name: 'Framework Info (if available)',
        link: routes.FRAMEWORK_INFO,
    }, {
        icon: { default: 'fa fa-paint-brush' },
        id: ids.COLLABORATE,
        collaborationEnabled: false,
        isActive,
        isEnabled: false,
        name: 'Collaborate with Others',
        link: routes.COLLABORATE,
        session: [],
    }, {
        icon: { default: <hr style={{ borderColor: 'black' }} /> },
        type: 'divider',
    }, {
        icon: { default: <span title={'Run the Transims-HISA Model'}><i className="material-icons">train</i></span> },
        id: ids.TRANSIMS,
        isActive,
        isEnabled: true,
        name: 'HISA (in development)',
        link: routes.TRANSIMS,
    }, {
        icon: { default: <span title={'Demographic Analysis'}><i className="material-icons">person</i></span> },
        id: ids.DEMOGRAPHIC_ANALYSIS,
        isActive,
        isEnabled: false,
        name: 'Demographic Analysis (in development)',
        link: routes.DEMOGRAPHIC_ANALYSIS,
    }, {
        icon: { default: <span title={'Refugee Model'}><img alt="transims" src="/static/er2_ficus/img/person_running.png" style={{ height: 24, width: 24 }} /></span> },
        id: ids.REFUGEE_MODEL,
        isActive,
        isEnabled: false,
        name: 'Refugee Model (in development)',
        link: routes.REFUGEE_MODEL,
    }, {
        icon: { default: 'fa fa-cog' },
        id: 'settings',
        isActive,
        name: 'Settings',
        link: routes.SETTINGS,
        position: 'bottom',
    }],
    token: '',
}

export default function contextBar(state: ContextBarItemType = initialState, action: any): ContextBarItemType {
    switch (action.type) {
    case actionTypes.CLICK_CONTEXT_BAR_ITEM:
        return {
            ...state,
            items: state.items.map(item => (
                item.id === action.id ?
                    { ...item, active: true } :
                    { ...item, active: false }
            )),
        }

    case actionTypes.RECEIVE_FICUS_DATA:
        return {
            ...state,
            items: state.items.map((item) => {
                if (item.id === ids.ANALYZE_FRAMEWORK) return { ...item, isEnabled: action.ficusNode.length > 0 }
                if (item.id === ids.COLLABORATE) return { ...item, isEnabled: action.ficusNode.length > 0 }
                if (item.id === ids.DEMOGRAPHIC_ANALYSIS) return { ...item, isEnabled: action.ficusNode.length > 0 }
                if (item.id === ids.REFUGEE_MODEL) return { ...item, isEnabled: action.ficusNode.length > 0 }
                // Check for framework documentation
                if (item.id === ids.FRAMEWORK_INFO) {
                    return { ...item, isEnabled: action.ficusData.frameworkInfo && action.ficusData.frameworkInfo.length > 0 }
                }
                return item
            }),
        }

    case actionTypes.RECEIVE_FICUS_FRAMEWORK_INFO:
        return {
            ...state,
            items: state.items.map(item => (
                item.id === ids.FRAMEWORK_INFO ? { ...item, isEnabled: action.frameworkInfo.length && action.frameworkInfo.length > 0 } : item
            )),
        }

    case actionTypes.SET_FETCHED_STATE:
        return {
            ...state,
            token: action.state.token.value,
        }

    case actionTypes.RECEIVE_SESSIONS:
    case actionTypes.RECEIVE_SHARE_SESSION:
    case actionTypes.RECEIVE_UNSHARE_SESSION:
        return {
            ...state,
            items: state.items.map(item => (
                item.id === ids.COLLABORATE ? {
                    ...item,
                    badge: {
                        content: getBadgeContent(action.sessions, item.collaborationEnabled, state.token),
                        color: getBadgeColor(item.sessions, action.enabled, state.token),
                        title: getBadgeContent(action.sessions, item.collaborationEnabled, state.token, true),
                    },
                    sessions: action.sessions,
                } : item
            )),
        }

    case actionTypes.RECEIVE_JOIN_SESSION:
    case actionTypes.RECEIVE_LEAVE_SESSION:
        return {
            ...state,
            items: state.items.map(item => (
                item.id === ids.COLLABORATE ? {
                    ...item,
                    badge: {
                        content: getBadgeContent(item.sessions, action.enabled, state.token),
                        color: getBadgeColor(item.sessions, action.enabled, state.token),
                        title: getBadgeContent(item.sessions, action.enabled, state.token, true),
                    },
                    collaborationEnabled: action.enabled,
                } : item
            )),
        }

    default:
        return state
    }
}
